import { Color4 } from './Color4';
import { Vector2 } from './Vector2';

/**
 * Тип фильтра. Активный вариант выбирается через {@link Filter.kind}, остальные поля
 * читаются только если они нужны выбранному kind.
 *
 * См. `packages/cgs-wge/docs/filters.md` для GLSL-формул и рекомендуемых значений.
 */
export enum FilterKind {
  /** Фильтр выключен (квад рисуется штатным шейдером без `#define FILTER`). */
  None = 0,
  /** Перекрашивание в `tintColor` через mix. После семплинга. */
  Tint = 1,
  /** Пикселизация UV до сетки `pixelSize` ячеек. До семплинга. */
  Pixelate = 2,
  /** Радиальная синусоидальная деформация UV вокруг `center`. До семплинга. Использует `time`. */
  Shockwave = 3,
  /** Монохромный шум поверх цвета. После семплинга. Использует `time`. */
  Noise = 4,
  /** Радиальное закручивание UV вокруг `center`. До семплинга. Параметр: `angle` × falloff. */
  Twist = 5,
  /** Радиальная выпуклость/вогнутость UV вокруг `center`. До семплинга. `strength`>0 — выпукло, <0 — вогнуто. */
  Bulge = 6,
  /** Хроматическая аберрация: каналы R/G/B семплятся со смещениями `redOffset`/`greenOffset`/`blueOffset`. */
  RGBSplit = 7,
  /** CRT-стилизация: горизонтальные scanlines (плотность через `pixelSize`) + vignette (`strength`) + intensity. После семплинга. */
  CRT = 8,
  /** Сепия (`sepia`) + вертикальные «царапины» (плотность через `intensity`, бегут по `time`). После семплинга. */
  OldFilm = 9,
  /** Обводка по краям непрозрачности. Цвет — `tintColor`, толщина — `radius`. 4 extra texture2D/pixel. */
  Outline = 10,
  /** Свечение внутрь от края: цвет — `tintColor`, ширина — `radius`. 8 extra texture2D/pixel. */
  InnerGlow = 11,
  /** Затемнение/тонирование углов кадра. Цвет — `tintColor`, начало — `radius`, мягкость — `strength`. */
  Vignette = 12,
  /** Радиальные лучи от точки `center`, цвет — `tintColor`, длина — `radius`. 16 extra texture2D/pixel. */
  GodRays = 13,
  /** Дешёвый bloom: яркие пиксели подсвечиваются. Порог яркости — `strength`, цвет — `tintColor`. 4 extra texture2D/pixel. */
  PseudoBloom = 14,
  /** Сдвиг оттенка по HSV. Угол сдвига — `angle` (радианы). После семплинга. */
  HueShift = 15,
  /** Постеризация: квантование цвета на N уровней. Уровни — `pixelSize` (4..32). После семплинга. */
  Posterize = 16,
  /**
   * Радиальная вспышка света: белёсое ядро + цветное гало с мягким затуханием + опциональное
   * кольцо линзового блика. Заменяет цвет квада расчётным светом (исходная текстура нужна
   * только как носитель альфы), альфа на выходе 0 — свет складывается со сценой аддитивно.
   * Центр — `center`, внешний радиус гало — `radius`, доля радиуса под ядро — `strength` (0..1),
   * цвет гало — `tintColor` (A — общая сила), яркость — `intensity` (анимировать для пульса).
   * Кольцо: радиус — `angle` (UV, 0 или ≥1 — выключено), ширина — `pixelSize` (% от радиуса
   * кольца), сила — `sepia`.
   */
  Flash = 17,
}

/**
 * Per-SceneObject shader filter. Кладётся в {@link SceneObject.filter}, после чего
 * `SceneObject.draw()` сам пушит его в `SpriteBatch` вместе с битом `Effect.Filter`.
 *
 * Один экземпляр можно переиспользовать на нескольких узлах. `time` для Shockwave/Noise/OldFilm/CRT
 * нужно обновлять покадрово (через update-loop / IFramePulse / FilterEditorPopup делает это
 * автоматически пока открыт).
 *
 * @example Tint — полу-красный тинт
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Tint;
 * f.tintColor = new Color4(1, 0, 0, 0.5);
 * icon.filter = f;
 * ```
 *
 * @example Pixelate
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Pixelate;
 * f.pixelSize = 48;
 * icon.filter = f;
 * ```
 *
 * @example Shockwave (нужно обновлять `time`)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Shockwave;
 * f.center = new Vector2(0.5, 0.5);
 * f.radius = 0.4;
 * f.strength = 1.0;
 * icon.filter = f;
 * const start = performance.now();
 * (function tick(){ f.time = (performance.now()-start)/1000; requestAnimationFrame(tick); })();
 * ```
 *
 * @example Noise
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Noise;
 * f.intensity = 0.2;
 * icon.filter = f;
 * (function tick(){ f.time = performance.now()/1000; requestAnimationFrame(tick); })();
 * ```
 *
 * @example Twist — закручивание
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Twist;
 * f.center = new Vector2(0.5, 0.5);
 * f.radius = 0.5;
 * f.angle = 2.0;            // радианы
 * icon.filter = f;
 * ```
 *
 * @example Bulge / Pinch
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Bulge;
 * f.center = new Vector2(0.5, 0.5);
 * f.radius = 0.5;
 * f.strength = 0.6;         // > 0 — выпукло, < 0 — вогнуто
 * icon.filter = f;
 * ```
 *
 * @example RGBSplit — хроматическая аберрация
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.RGBSplit;
 * f.redOffset = new Vector2(0.005, 0);
 * f.greenOffset = new Vector2(0, 0);
 * f.blueOffset = new Vector2(-0.005, 0);
 * icon.filter = f;
 * ```
 *
 * @example CRT
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.CRT;
 * f.pixelSize = 240;        // плотность scanlines
 * f.intensity = 0.6;        // глубина scanlines
 * f.strength = 0.7;         // интенсивность vignette
 * icon.filter = f;
 * (function tick(){ f.time = performance.now()/1000; requestAnimationFrame(tick); })();
 * ```
 *
 * @example OldFilm
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.OldFilm;
 * f.sepia = 0.7;            // насыщенность сепии
 * f.intensity = 0.4;        // плотность царапин
 * icon.filter = f;
 * (function tick(){ f.time = performance.now()/1000; requestAnimationFrame(tick); })();
 * ```
 *
 * @example Outline — обводка контура (4 extra samples)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Outline;
 * f.tintColor = new Color4(1, 1, 0, 1);   // желтая обводка
 * f.radius = 0.01;                          // толщина в UV
 * icon.filter = f;
 * ```
 *
 * @example InnerGlow — внутреннее свечение (8 extra samples)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.InnerGlow;
 * f.tintColor = new Color4(0, 1, 1, 1);    // голубое неоновое свечение
 * f.radius = 0.05;                           // ширина свечения
 * f.intensity = 1.0;
 * icon.filter = f;
 * ```
 *
 * @example Vignette — затемнение углов (cheap)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Vignette;
 * f.tintColor = new Color4(0, 0, 0, 1);    // черные углы
 * f.radius = 0.3;                            // от 0.3 до 0.8 — переход
 * f.strength = 0.5;                          // мягкость градиента
 * f.intensity = 1.0;
 * icon.filter = f;
 * ```
 *
 * @example GodRays — лучи света (16 extra samples — ДОРОГО)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.GodRays;
 * f.center = new Vector2(0.5, 0.0);        // источник наверху
 * f.tintColor = new Color4(1, 0.9, 0.6, 1); // тёплый свет
 * f.intensity = 1.5;
 * icon.filter = f;
 * ```
 *
 * @example PseudoBloom — дешёвый bloom (4 extra samples)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.PseudoBloom;
 * f.tintColor = new Color4(1, 1, 1, 1);
 * f.strength = 0.5;          // яркость от которой считается bloom
 * f.radius = 0.005;          // ширина размытия
 * f.intensity = 1.5;
 * icon.filter = f;
 * ```
 *
 * @example HueShift (cheap)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.HueShift;
 * f.angle = 1.5;             // радианы
 * icon.filter = f;
 * ```
 *
 * @example Posterize (cheap)
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Posterize;
 * f.pixelSize = 6;           // 6 уровней на канал
 * icon.filter = f;
 * ```
 *
 * @example Flash — радиальная вспышка света (cheap). Вешать на отдельный квад
 * с выделенной (не атласной) белой текстурой, позиционировать самим SceneObject.
 * ```ts
 * const f = new Filter();
 * f.kind = FilterKind.Flash;
 * f.center = new Vector2(0.5, 0.5); // центр квада
 * f.radius = 0.5;                    // гало до краёв квада
 * f.strength = 0.25;                 // ядро — четверть радиуса
 * f.tintColor = new Color4(1.0, 0.85, 0.95, 1.0); // розоватое гало
 * flashQuad.filter = f;
 * // пульс: анимировать f.intensity (0 — вспышка погасла, квад невидим)
 * ```
 *
 * @example Снять фильтр
 * ```ts
 * icon.filter = null;
 * ```
 */
export class Filter {
  /** Какой эффект активен. Если `None` — фильтр игнорируется в шейдере. */
  kind: FilterKind = FilterKind.None;

  /** Время в секундах. Обновлять покадрово для Shockwave/Noise/CRT/OldFilm. */
  time: number = 0;

  /** Общий множитель силы эффекта. Используется в Tint, Noise, CRT (depth), OldFilm (scratch). */
  intensity: number = 1.0;

  /** Цвет тинта для `FilterKind.Tint`. RGB — цвет, A — сила (0..1). */
  tintColor: Color4 = new Color4(1, 1, 1, 1);

  /** `Pixelate`: число ячеек на UV. `CRT`: плотность scanlines. `Flash`: ширина кольца в % от его радиуса. */
  pixelSize: number = 8.0;

  /** Центр эффекта в UV [0..1] для Shockwave/Twist/Bulge/Flash. */
  center: Vector2 = new Vector2(0.5, 0.5);

  /** Радиус действия в UV-единицах для Shockwave/Twist/Bulge/Flash. */
  radius: number = 0.5;

  /** Shockwave: амплитуда волны. Bulge: сила деформации (>0 выпукло, <0 вогнуто). CRT: интенсивность vignette. Flash: доля радиуса под белёсое ядро (0..1). */
  strength: number = 0.5;

  /** Twist: угол закручивания (радианы), умножается на falloff внутри радиуса. Flash: радиус кольца в UV (0 или ≥1 — кольца нет). */
  angle: number = 1.0;

  /** RGBSplit: смещение UV для красного канала. */
  redOffset: Vector2 = new Vector2(0.005, 0);

  /** RGBSplit: смещение UV для зелёного канала. */
  greenOffset: Vector2 = new Vector2(0, 0);

  /** RGBSplit: смещение UV для синего канала. */
  blueOffset: Vector2 = new Vector2(-0.005, 0);

  /** OldFilm: насыщенность сепии (0..1). Flash: яркость кольца (0 — кольца нет). */
  sepia: number = 0.7;
}
